/**
 * Session context: the authenticated identity, the internal role the backend resolved, and the
 * shared API client that carries the bearer token.
 *
 * Two identities are deliberately kept apart:
 *   the provider session (Supabase: e-mail, AAL) and
 *   the internal actor (`GET /me`: userId, displayName, activeRole),
 * because our database decides the role, not the token's claims (AUTHORIZATION-MATRIX §1). The UI
 * reads `activeRole` only to decide what to *offer*; every command is still authorized
 * server-side.
 *
 * `mfaSatisfied` still arrives on `/me` and is still carried on the provider session as `aal`.
 * Neither is read for a decision anywhere in this application: PO 7.10 made a verified address and
 * a password the requirement, and the fields stay because they are true, not because they gate.
 */
import {
  createContext,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ApiClient, readEnvConfig } from '../api/client';
import { type ApiError, isApiError } from '../api/errors';
import { whoAmI } from '../api/internal';
import type { InternalRole, WhoAmI } from '../api/types';
import type { AuthAdapter, AuthState, RecoveryGrant, RecoverySession } from './adapter';
import { SIGNED_OUT } from './adapter';
import { createAuthAdapter } from './createAdapter';

export interface SessionContextValue {
  api: ApiClient;
  auth: AuthState;
  actor: WhoAmI | null;
  /** True until the first restore + `/me` round trip settles. */
  loading: boolean;
  /** Set when a request came back 401 while the app believed it was signed in. */
  sessionExpired: boolean;
  /** The internal actor could not be resolved (token valid, user not provisioned). */
  unknownUser: boolean;
  role: InternalRole | null;
  providerName: string;
  signIn(email: string, password: string): Promise<AuthState>;
  signOut(): Promise<void>;
  refreshActor(): Promise<void>;

  /*
   * Password recovery, passed through rather than folded in.
   *
   * These three are the only members of this context that do NOT touch `auth`, `actor` or
   * `sessionExpired`, and that is the point: a recovery link establishes a session that may set a
   * password and nothing else. Routing it through `applyState` would make the console's own
   * session out of it — a session with no second factor, reached by e-mail. The screen therefore
   * reads its own outcome from these calls and this provider learns nothing from them.
   */
  supportsRecovery: boolean;
  /**
   * Asks for a recovery mail and learns nothing from it — not even whether the address is known.
   *
   * It is in this group rather than beside `signIn` for the same reason the other three are: it
   * touches no session. A person who asks for a mail is still signed out when it returns.
   */
  requestPasswordRecovery(email: string): Promise<void>;
  beginPasswordRecovery(grant: RecoveryGrant): Promise<RecoverySession>;
  completePasswordRecovery(newPassword: string): Promise<void>;
  endPasswordRecovery(): Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}

/** Convenience for permission-shaped UI decisions. Never a substitute for backend authorization. */
export function useRole(): { role: InternalRole | null; isOneOf: (...roles: InternalRole[]) => boolean } {
  const { role } = useSession();
  return {
    role,
    isOneOf: (...roles: InternalRole[]) => (role ? roles.includes(role) : false),
  };
}

export function SessionProvider({
  children,
  adapter: injectedAdapter,
  client: injectedClient,
}: {
  children: ReactNode;
  /** Tests inject their own adapter/client; the app builds them from the public env config. */
  adapter?: AuthAdapter;
  client?: ApiClient;
}): ReactElement {
  const env = readEnvConfig();
  const [auth, setAuth] = useState<AuthState>(SIGNED_OUT);
  const [actor, setActor] = useState<WhoAmI | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [unknownUser, setUnknownUser] = useState(false);

  const adapter = useMemo(
    () =>
      injectedAdapter ??
      createAuthAdapter({
        provider: import.meta.env.VITE_AUTH_PROVIDER,
        supabaseUrl: env.supabaseUrl,
        supabaseAnonKey: env.supabaseAnonKey,
        harnessEndpoint: import.meta.env.VITE_AUTH_HARNESS_ENDPOINT,
        isProduction: import.meta.env.PROD,
      }),
    [injectedAdapter, env.supabaseUrl, env.supabaseAnonKey],
  );

  const adapterRef = useRef(adapter);
  adapterRef.current = adapter;

  /*
   * Re-reading who we are, when the server says we are not who we thought.
   *
   * `loadActor` is built from `api` and `api` wants to call it back on a 403, so the callback goes
   * through a ref rather than through the dependency graph. The guard is not decoration: one screen
   * can fire several protected reads at once, and a role change would otherwise answer a burst of
   * 403s with a burst of `/me` calls.
   */
  const staleActorRef = useRef<(() => Promise<void>) | null>(null);
  const refreshingRef = useRef(false);
  const onForbidden = useCallback(() => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    void Promise.resolve(staleActorRef.current?.()).finally(() => {
      refreshingRef.current = false;
    });
  }, []);

  const api = useMemo(
    () =>
      injectedClient ??
      new ApiClient({
        baseUrl: env.apiBaseUrl,
        getAccessToken: () => adapterRef.current.getAccessToken(),
      }),
    [injectedClient, env.apiBaseUrl],
  );

  /*
   * «We do not know this account» is not «your session ended», and conflating the two is what tore
   * down a working session.
   *
   * `resolveActor` answers 401 for both: an expired or unverifiable token, and a perfectly good
   * token whose subject has no internal user yet — which is every session on an installation whose
   * first owner has not been created. Treating the second as an expiry put the person on «Oturum
   * süresi doldu», whose only control signs them out — destroying the very session the first-owner
   * setup needs. The provisioning case is left to `loadActor`, which has the code and says so on a
   * screen that keeps the session and offers to finish the setup with it.
   */
  const onUnauthenticated = useCallback((error: ApiError) => {
    if (error.code === 'unknown_internal_user') return;
    setSessionExpired(true);
  }, []);

  /* Attached to whichever client this provider ended up with, built here or handed in. */
  useEffect(() => api.onForbidden(onForbidden), [api, onForbidden]);
  useEffect(() => api.onUnauthenticated(onUnauthenticated), [api, onUnauthenticated]);

  const loadActor = useCallback(
    async (state: AuthState) => {
      if (!state.hasToken) {
        setActor(null);
        setUnknownUser(false);
        return;
      }
      try {
        const me = await whoAmI(api);
        setActor(me);
        setUnknownUser(false);
        setSessionExpired(false);
      } catch (error) {
        setActor(null);
        // a valid provider token whose user we do not know is a provisioning problem, not an
        // expired session: the shell says so instead of bouncing to the login form
        const unprovisioned = isApiError(error) && error.code === 'unknown_internal_user';
        setUnknownUser(unprovisioned);
        /* And it is not an expiry, so nothing earlier in this round may have marked it as one. */
        if (unprovisioned) setSessionExpired(false);
      }
    },
    [api],
  );

  const authRef = useRef(auth);
  authRef.current = auth;
  staleActorRef.current = () => loadActor(authRef.current);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const restored = await adapter.restore();
      if (cancelled) return;
      setAuth(restored);
      await loadActor(restored);
      if (!cancelled) setLoading(false);
    })();
    const unsubscribe = adapter.onChange((state) => {
      setAuth(state);
      void loadActor(state);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [adapter, loadActor]);

  const applyState = useCallback(
    async (state: AuthState) => {
      setAuth(state);
      setSessionExpired(false);
      await loadActor(state);
      return state;
    },
    [loadActor],
  );

  const value = useMemo<SessionContextValue>(
    () => ({
      api,
      auth,
      actor,
      loading,
      sessionExpired,
      unknownUser,
      role: actor?.activeRole ?? null,
      providerName: adapter.name,
      signIn: async (email, password) => applyState(await adapter.signInWithPassword(email, password)),
      signOut: async () => {
        await adapter.signOut();
        setAuth(SIGNED_OUT);
        setActor(null);
        setSessionExpired(false);
        setUnknownUser(false);
      },
      refreshActor: async () => {
        await loadActor(auth);
      },
      /* Straight through to the adapter: no `applyState`, so nothing here can become a session. */
      supportsRecovery: adapter.supportsRecovery,
      requestPasswordRecovery: (email) => adapter.requestPasswordRecovery(email),
      beginPasswordRecovery: (grant) => adapter.beginPasswordRecovery(grant),
      completePasswordRecovery: (newPassword) => adapter.completePasswordRecovery(newPassword),
      endPasswordRecovery: () => adapter.endPasswordRecovery(),
    }),
    [api, auth, actor, loading, sessionExpired, unknownUser, adapter, applyState, loadActor],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
